import { Box, Center, Group, Text } from '@mantine/core';
import { facingModeFromLocalTrack, type LocalVideoTrack } from 'livekit-client';
import { type ReactNode, useEffect, useRef } from 'react';

import { UserAvatar } from '@/shared/user-avatar';

type CameraPreviewProps = {
    track: LocalVideoTrack | undefined;
    name: string;
    controls: ReactNode;
};

export function CameraPreview({ track, name, controls }: Readonly<CameraPreviewProps>) {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const element = videoRef.current;
        if (!element || !track) return;
        track.attach(element);
        return () => {
            track.detach(element);
        };
    }, [track]);

    const mirrored = track !== undefined && facingModeFromLocalTrack(track).facingMode !== 'environment';

    return (
        <Box
            pos="relative"
            w="100%"
            style={{
                aspectRatio: '16 / 9',
                overflow: 'hidden',
                border: '1px solid var(--color-border-default)',
                borderRadius: 'var(--radius-rounded-3xl)',
                boxShadow: 'var(--shadow-md)',
                backgroundColor: 'var(--color-surface-1)',
            }}
        >
            <video
                ref={videoRef}
                muted
                playsInline
                style={{
                    display: track ? 'block' : 'none',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transform: mirrored ? 'scaleX(-1)' : undefined,
                }}
            />
            {!track && (
                <Center inset={0} pos="absolute">
                    <UserAvatar
                        name={name}
                        size="var(--spacing-24)"
                        styles={{ placeholder: { fontSize: 'var(--heading-2-font-size)' } }}
                    />
                </Center>
            )}
            {name && (
                <Box
                    left="var(--spacing-4)"
                    maw="calc(100% - var(--spacing-8))"
                    pos="absolute"
                    px="var(--spacing-3)"
                    py="var(--spacing-1)"
                    style={{ borderRadius: 'var(--radius-rounded-full)', backgroundColor: 'var(--color-default-default)' }}
                    top="var(--spacing-4)"
                >
                    <Text fw={500} size="xs" truncate="end">
                        {name}
                    </Text>
                </Box>
            )}
            <Group
                bottom="var(--spacing-3)"
                gap="var(--spacing-4)"
                left="50%"
                pos="absolute"
                style={{ transform: 'translateX(-50%)' }}
                wrap="nowrap"
            >
                {controls}
            </Group>
        </Box>
    );
}
